import { z } from "zod";
import { DEFAULT_APPROVED_LLM_PAIR, GEMINI_LEGACY_PERSISTED_MODEL_ID, GEMINI_PREVIOUS_APPROVED_MODEL_ID, DEFAULT_OPENAI_APPROVED_PAIR, isApprovedLlmPair, resolveApprovedLlmPair } from "./llmModelRegistry.js";
import { NonEmptyStringSchema, UnknownRecordSchema } from "./source.js";
// The single live Gemini model pin. Every new Gemini call resolves to this id;
// persisted artifacts may still contain older pins for read compatibility.
//
// The literal id, its request limits, and its generation-config version now
// live in `llmModelRegistry.ts` (`APPROVED_LLM_MODELS`) — the sole source of
// truth for approved provider/model pairs. Everything below is a thin,
// behaviour-preserving wrapper over that registry so existing Gemini-only
// callers compile and run unchanged.
export const GEMINI_ACTIVE_MODEL_ID = DEFAULT_APPROVED_LLM_PAIR.modelId;
// S1 verification follow-up #5: this was a private const here, which left the
// registry short of being the sole source of per-model capability. The literal
// (1,048,576) moved into `llmModelRegistry.ts` verbatim and is now read back,
// so every derived figure below is numerically unchanged.
const GEMINI_ACTIVE_MODEL_INPUT_TOKEN_LIMIT = DEFAULT_APPROVED_LLM_PAIR.inputTokenLimit;
const GEMINI_ACTIVE_MODEL_INPUT_TOKEN_SAFETY_DIVISOR = 10;
/**
 * Provider-published limits and request-admission inputs used before a live
 * request is sent. Keep these beside the active model pin so changing the live
 * model cannot leave request admission bound to stale capabilities.
 *
 * The estimated bytes/token value is diagnostic only: it is not a safe upper
 * bound because byte-fallback tokenization can approach one token per UTF-8
 * byte. Admission guards must use a one-byte/token fast-path proof or an exact
 * provider token count.
 */
export const GEMINI_ACTIVE_MODEL_REQUEST_LIMITS = {
    model: GEMINI_ACTIVE_MODEL_ID,
    inputTokenLimit: GEMINI_ACTIVE_MODEL_INPUT_TOKEN_LIMIT,
    totalRequestBytesLimit: DEFAULT_APPROVED_LLM_PAIR.totalRequestBytesLimit,
    estimatedBytesPerInputToken: 3,
    inputTokenSafetyHeadroom: Math.ceil(GEMINI_ACTIVE_MODEL_INPUT_TOKEN_LIMIT /
        GEMINI_ACTIVE_MODEL_INPUT_TOKEN_SAFETY_DIVISOR)
};
/**
 * Keep supported runtime model overrides from reopening an older live-call
 * path. Callers may retain the requested value for diagnostics, but the
 * configured call model always remains the active pin above.
 *
 * Deprecated: a Gemini-only alias over the generalized
 * `resolveApprovedLlmPair` (`llmModelRegistry.ts`), which performs the same
 * clamp for any approved provider/model pair. Kept for the ~11 existing
 * Gemini provider classes; new call sites should resolve against the
 * registry directly.
 */
export function clampGeminiActiveModel(requestedModel) {
    const resolved = resolveApprovedLlmPair({ modelId: requestedModel });
    if (!resolved.clamped || resolved.requestedModelId === undefined) {
        return {
            model: GEMINI_ACTIVE_MODEL_ID,
            clamped: false
        };
    }
    return {
        model: GEMINI_ACTIVE_MODEL_ID,
        requestedModel: resolved.requestedModelId,
        clamped: true
    };
}
export function resolveGeminiActiveModel(requestedModel) {
    return clampGeminiActiveModel(requestedModel).model;
}
/**
 * The provider-layer choke point for every live LLM SDK call: rewrite the
 * outgoing request's model to the given approved pair's pinned id, and throw
 * if that pair is not approved. Keeping this enforcement around the client
 * makes retries and future request-building paths unable to bypass it.
 *
 * Generalizes `withGeminiActiveModelCallBoundary` (below, now a deprecated
 * alias bound to the Gemini default pair) to any approved provider/model
 * pair from `llmModelRegistry.ts`, so none of the existing provider classes
 * need to change to keep their current Gemini-pinned behaviour.
 */
export function withApprovedModelCallBoundary(client, pair) {
    if (!isApprovedLlmPair(pair.provider, pair.modelId)) {
        throw new Error(`LLM provider/model pair is not approved: provider="${pair.provider}" modelId="${pair.modelId}". ` +
            "Approved pairs are declared in packages/schemas/src/llmModelRegistry.ts.");
    }
    return {
        models: {
            generateContent(input) {
                return client.models.generateContent({
                    ...input,
                    model: pair.modelId
                });
            }
        }
    };
}
/**
 * Deprecated: a Gemini-only alias over `withApprovedModelCallBoundary` bound
 * to the registry's default (Gemini) pair. Behaviour is unchanged — every
 * call is still rewritten to the single active Gemini pin regardless of the
 * caller-supplied model.
 */
export function withGeminiActiveModelCallBoundary(client) {
    return withApprovedModelCallBoundary(client, {
        provider: DEFAULT_APPROVED_LLM_PAIR.provider,
        modelId: GEMINI_ACTIVE_MODEL_ID
    });
}
// Model ids that may legitimately appear on a PERSISTED artifact. The active pin
// is what we write now; earlier pins stay readable so Firestore records written
// by previous deploys still validate on read (firestoreStore re-parses stored
// documents through these schemas). Never remove an id that reached production.
export const GEMINI_PERSISTED_MODEL_IDS = [
    GEMINI_LEGACY_PERSISTED_MODEL_ID,
    GEMINI_PREVIOUS_APPROVED_MODEL_ID,
    GEMINI_ACTIVE_MODEL_ID
];
export const PersistedGeminiModelIdSchema = z.enum(GEMINI_PERSISTED_MODEL_IDS);
export const PROVIDER_MODEL_NOT_REPORTED = "provider-model-not-reported";
export const ProviderModelIdSchema = NonEmptyStringSchema;
export const GEMINI_PINNED_GENERATION_CONFIG_VERSION = DEFAULT_APPROVED_LLM_PAIR.generationConfigVersion;
export const GEMINI_PINNED_GENERATION_CONFIG = {
    temperature: 0,
    topP: 1,
    topK: 1,
    seed: 20260703,
    candidateCount: 1,
    presencePenalty: 0,
    frequencyPenalty: 0
};
export const GEMINI_SPEC_AGENT_GENERATION_CONFIG = {
    ...GEMINI_PINNED_GENERATION_CONFIG,
    thinkingConfig: {
        thinkingBudget: 2_048
    }
};
export const SPEC_AGENT_PROVIDER_CALL_ARCHITECTURE = "two-call-compose-v1";
export const GEMINI_OVERSEER_GENERATION_CONFIG = {
    ...GEMINI_PINNED_GENERATION_CONFIG,
    thinkingConfig: {
        thinkingBudget: 1_024
    }
};
export const GEMINI_GLOBAL_AGGREGATOR_GENERATION_CONFIG = {
    ...GEMINI_PINNED_GENERATION_CONFIG,
    thinkingConfig: {
        thinkingBudget: 512
    }
};
export const GEMINI_REQUIREMENT_INTERPRETATION_GENERATION_CONFIG = {
    ...GEMINI_PINNED_GENERATION_CONFIG,
    thinkingConfig: {
        thinkingBudget: 512
    }
};
export const GEMINI_PROJECT_EXTRACTION_GENERATION_CONFIG = {
    ...GEMINI_PINNED_GENERATION_CONFIG,
    thinkingConfig: {
        thinkingBudget: 1_024
    }
};
export const GEMINI_POLICY_FLAG_DISCOVERY_GENERATION_CONFIG = {
    ...GEMINI_PINNED_GENERATION_CONFIG,
    thinkingConfig: {
        thinkingBudget: 1_024
    }
};
export const GEMINI_SCHEDULE_GENERATION_CONFIG = {
    ...GEMINI_PINNED_GENERATION_CONFIG,
    thinkingConfig: {
        thinkingBudget: 512
    }
};
export const GEMINI_PINNED_GENERATION_CONFIG_JUSTIFICATION = [
    "temperature=0 uses the provider-documented minimum randomness for a schema-constrained, verifier-repaired specification task.",
    "topK=1 pins decoding to the highest-probability token set; the active model's verified metadata supports the pinned generation surface.",
    "topP=1 keeps nucleus filtering non-truncating while topK and temperature provide the determinism pressure, reducing answer-quality loss from extra probability clipping.",
    "seed=20260703 is supported by @google/genai GenerateContentConfig and the Gemini API as a best-effort repeatability hint, not a determinism guarantee.",
    "candidateCount=1 and zero presence/frequency penalties remove implicit output-variant and penalty defaults from the sampling surface."
];
export const GEMINI_PINNED_GENERATION_CONFIG_SUPPORT = {
    sdkPackage: "@google/genai",
    sdkVersion: "2.9.0",
    model: GEMINI_ACTIVE_MODEL_ID,
    modelVersionObserved: GEMINI_ACTIVE_MODEL_ID,
    modelMetadataObservedAt: "2026-08-17",
    seed: {
        supported: true,
        semantics: "best_effort_repeatability"
    },
    topK: {
        supported: true,
        observedModelTopK: 64
    }
};
/**
 * The OpenAI pair's equivalent of {@link GEMINI_PINNED_GENERATION_CONFIG_VERSION}.
 *
 * It is a *separate* version string on purpose
 * (`_build/LLM-PROVIDER-PLAN-2026-07-31.md` design decision §4): the OpenAI pair
 * is not sampling-pinned the way the Gemini pair is, because the Responses API
 * rejects `temperature`/`topP`/`topK`/`seed`/`candidateCount`/penalties on
 * reasoning models, so the adapter drops them and records the drop
 * (`openAiClient.ts`'s `OPENAI_DROPPED_GENERATION_PARAMETERS`). Stamping the
 * Gemini config version onto an OpenAI call would claim a determinism posture
 * the call does not have — and would make the two providers' `configVersion`
 * compare equal, which is the opposite of what the anchor comparison needs.
 */
export const OPENAI_PINNED_GENERATION_CONFIG_VERSION = DEFAULT_OPENAI_APPROVED_PAIR.generationConfigVersion;
export const OPENAI_PINNED_GENERATION_CONFIG_JUSTIFICATION = [
    "The Responses API rejects temperature/topP/topK/candidateCount and presence/frequency penalties on reasoning models, so the canonical request's pinned sampling parameters are dropped at the adapter boundary and recorded in guardrailNotes rather than sent.",
    "seed has no Responses API equivalent (Chat Completions' seed is best-effort and is not honoured on reasoning models), so this pair carries no repeatability hint at all — see seedSupport below.",
    "Determinism pressure therefore comes from the strict json_schema response format and reasoning.effort alone, not from the sampling surface.",
    "reasoning.effort is derived from the canonical thinkingConfig.thinkingBudget through the approved pair's registry-declared ladder, so a per-call thinking budget still maps to a reviewable, model-specific effort level.",
    "store:false is set on every request, so no tender content is retained by the provider for retrieval."
];
/**
 * Mirrors {@link GEMINI_PINNED_GENERATION_CONFIG_SUPPORT}'s shape so a
 * provider-aware fingerprint builder can select between them by provider.
 *
 * `seed.supported: false` / `semantics: "not_available"` is the field the plan
 * calls out by name: it is what makes a cross-provider prior-decision anchor
 * fall out as `"incomparable"` rather than silently comparing a sampling-pinned
 * Gemini decision against a non-pinned OpenAI one.
 */
export const OPENAI_PINNED_GENERATION_CONFIG_SUPPORT = {
    sdkPackage: "openai",
    sdkVersion: "7.3.0",
    model: DEFAULT_OPENAI_APPROVED_PAIR.modelId,
    modelMetadataObservedAt: "2026-07-31",
    seed: {
        supported: false,
        semantics: "not_available"
    },
    samplingParameters: {
        supported: false,
        semantics: "dropped_at_adapter_boundary"
    }
};
export const GeminiPinnedGenerationConfigSchema = z
    .object({
    temperature: z.literal(0),
    topP: z.literal(1),
    topK: z.literal(1),
    seed: z.literal(20260703),
    candidateCount: z.literal(1),
    presencePenalty: z.literal(0),
    frequencyPenalty: z.literal(0)
})
    .strict();
export const ProviderAttemptIdentitySchema = z
    .object({
    id: NonEmptyStringSchema,
    logicalCallId: NonEmptyStringSchema,
    attemptOrdinal: z.number().int().positive()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.id !== `${value.logicalCallId}/attempt-${value.attemptOrdinal}`) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "id must derive from logicalCallId and attemptOrdinal",
            path: ["id"]
        });
    }
});
export const ProviderRequestFingerprintSchema = z
    .object({
    providerId: NonEmptyStringSchema,
    model: NonEmptyStringSchema,
    requestFingerprint: NonEmptyStringSchema,
    attemptIdentity: ProviderAttemptIdentitySchema.optional(),
    configFingerprint: NonEmptyStringSchema,
    configVersion: NonEmptyStringSchema,
    providerCallArchitecture: NonEmptyStringSchema.optional(),
    generationConfig: UnknownRecordSchema,
    guardrailNotes: z.array(NonEmptyStringSchema).optional(),
    promptHash: NonEmptyStringSchema.optional(),
    systemInstructionHash: NonEmptyStringSchema.optional(),
    responseJsonSchemaHash: NonEmptyStringSchema.optional(),
    metadata: UnknownRecordSchema.optional(),
    payloadEvidence: z
        .object({
        persistenceVersion: z.literal("provider-payload-v1"),
        prompt: z
            .object({
            contentHash: NonEmptyStringSchema,
            byteLength: z.number().int().nonnegative(),
            persistedByteLength: z.number().int().nonnegative().optional(),
            maxInlineBytes: z.number().int().positive().optional(),
            storage: z.enum(["inline", "truncated_head_tail"]).optional(),
            truncated: z
                .object({
                headByteLength: z.number().int().nonnegative(),
                tailByteLength: z.number().int().nonnegative(),
                omittedByteLength: z.number().int().nonnegative()
            })
                .strict()
                .optional(),
            text: z.string()
        })
            .strict(),
        rawModelText: z
            .object({
            contentHash: NonEmptyStringSchema,
            byteLength: z.number().int().nonnegative(),
            persistedByteLength: z.number().int().nonnegative().optional(),
            maxInlineBytes: z.number().int().positive().optional(),
            storage: z.enum(["inline", "truncated_head_tail"]).optional(),
            truncated: z
                .object({
                headByteLength: z.number().int().nonnegative(),
                tailByteLength: z.number().int().nonnegative(),
                omittedByteLength: z.number().int().nonnegative()
            })
                .strict()
                .optional(),
            text: z.string()
        })
            .strict()
            .optional(),
        redaction: z
            .object({
            apiKeyPersisted: z.literal(false),
            authHeadersPersisted: z.literal(false)
        })
            .strict()
    })
        .strict()
        .optional(),
    seedSupport: UnknownRecordSchema.optional()
})
    .strict();
//# sourceMappingURL=providerConfig.js.map